import { Button, Checkbox, Tooltip } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { ArrowDownIcon, DownloadIcon, SearchIcon } from "../../../Assets/images/svgs";
import { ellipsisify } from "../../../Library/helpers";
import "./style.css";
import { Title } from "../../Atoms";

const ReactTable = ({ selectedVids = [], data, columns, showOverlay, showSelection, showSearch, onRowSelect, pageSize = 5, heading, buttonName, buttonHandler = () => { }, showSelectAll = true, checkboxLabel = "", showDownload, downloadHandler = () => { }, onPageChange, autoResetPage = false, totalItems }) => {
  const [pagintionInputVal, setPagintionInputVal] = useState(1);
  const prevSelectedIdsRef = React.useRef([]);

  const selectedRowIds = React.useMemo(() => {
    if (!Array.isArray(selectedVids)) return {};
    const selectedSet = new Set(selectedVids.map(String));
    return data.reduce((acc, row, index) => {
      if (row.idx !== undefined && row.idx !== null && selectedSet.has(String(row.idx))) {
        acc[index] = true;
      }
      return acc;
    }, {});
  }, [selectedVids, data]);

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  function DefaultColumnFilter({ column: { filterValue, setFilter } }) {

    return (
      <div className="table-search-container">
        {!filterValue ? <div className="search-overlay">
          <SearchIcon />
          Search
        </div> : null}
        <input
          value={filterValue || ""}
          onChange={(e) => {
            setFilter(e.target.value || undefined);
          }}
          className="table-search-input"
        />
      </div>
    );
  }

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef()
      const resolvedRef = ref || defaultRef

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
      }, [resolvedRef, indeterminate])

      rest["isChecked"] = !rest.disabled && rest.checked;
      return (
        <>
          <Checkbox className={`select-row-input ${rest.disabled ? "disabled-checkbox" : ""}`} ref={resolvedRef} {...rest} ></Checkbox>
        </>
      )
    }
  )

  const isManual = totalItems !== undefined;
  const calculatedPageCount = isManual
    ? Math.ceil(totalItems / pageSize)
    : Math.ceil((data?.length || 0) / pageSize);

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, selectedFlatRows, state: { pageIndex }, pageCount, canPreviousPage, canNextPage, nextPage, previousPage, gotoPage } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      getRowId: (_, index) => index,
      initialState: { pageSize: pageSize, selectedRowIds },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      manualPagination: isManual,
      pageCount: calculatedPageCount,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (!showSelection) return;
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          type: 'select',
          Header: ({ getToggleAllRowsSelectedProps }) => {
            return showSelectAll ? (<div>
              <label className="select-all-checkbox-label">
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                <p className="select-all-label">{checkboxLabel}</p>
              </label>
            </div>) : null;
          },
          Cell: ({ row }) => (
            <div>
              {row.original.firstCol ? row.original.firstCol : <IndeterminateCheckbox disabled={row.original.disabled} {...row.getToggleRowSelectedProps()} />}
            </div>
          ),
        },
        ...columns,
      ])
    },
  );

  useEffect(() => {
    if (!onRowSelect) return;

    const ids = (selectedFlatRows || []).map(r => r?.original?.idx ?? r.id).filter(id => id !== undefined && id !== null);

    const newSet = new Set(ids);
    const prevSet = prevSelectedIdsRef.current;

    let changed = false;
    if (newSet.size !== prevSet.size) {
      changed = true;
    } else {
      for (const id of newSet) {
        if (!prevSet.has(id)) { changed = true; break; }
      }
    }

    if (!changed) return;

    prevSelectedIdsRef.current = newSet;
    const allSelected = (data?.length ?? 0) && ids.length === data.length;
    onRowSelect(allSelected, selectedFlatRows);
  }, [selectedFlatRows, onRowSelect, data]);

  const manageSelectedRows = () => {
    if (!onRowSelect) return;
    if (data.length === selectedFlatRows.length) onRowSelect(true);
    else onRowSelect(false, selectedFlatRows)
  }

  const handlePaginationInputChange = (e) => {
    const value = e.target.value;
    setPagintionInputVal(value);

    if (value) {
      const page = Number(value) - 1;
      if (!isNaN(page) && page >= 0 && page < pageCount) {
        gotoPage(page);
      }
    }
  };

  useEffect(() => {
    setPagintionInputVal(pageIndex + 1);
  }, [pageIndex])

  useEffect(() => {
    if (onPageChange) onPageChange(pageIndex);
  }, [pageIndex, onPageChange]);

  const isManualPagination = totalItems !== undefined;

  return (
    <div>
      <div className={'data-table-container'}>
        {
          (heading || buttonName) ? <div className="data-table-header">
            {heading ? <Title>{heading}</Title> : null}
            {buttonName ? <Button variant={"solid"} onClick={buttonHandler}>{buttonName}</Button> : null}
            {showDownload ? <DownloadIcon className="down-icon" onClick={downloadHandler} /> : null}
          </div> : null
        }
        <table className="data-table" {...getTableProps()}>
          <thead onClick={manageSelectedRows}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <th className={`table-heading-cell ${column.isSorted ? "align-icon" : ""}`} {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <Tooltip label={`${column.label ? column.label : (column.Header.length > 12 ? column.Header : "")}`}>
                      <div>
                        {idx ? ellipsisify(column.Header, 16) : column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? <ArrowDownIcon className="table-sort-icon" />
                              : <ArrowDownIcon className="table-sort-icon rotated" />
                            : ""}
                        </span>
                      </div>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            ))}
            {showSearch && headerGroups.map((headerGroup) => (
              <tr className="table-filter" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    <div>{column.canFilter ? column.render("Filter") : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(() => {
              const rowsToDisplay = (isManualPagination && page.length > pageSize) ? page.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) : page;
              return rowsToDisplay.length > 0 ? rowsToDisplay.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className={selectedRowIds[row.index] ? "selected-row" : ""}>
                    {row.cells.map((cell) => {
                      return (
                        <td className="table-data-cell" {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      )
                    })}
                  </tr>
                );
              }) : <tr className="no-data-avilable"><td colSpan={99}>No Data Available</td></tr>;
            })()}
          </tbody>
        </table>
      </div>

      {showOverlay && <div className="popover-overlay"></div>}

      {
        pageCount > 1 ? <div className="table-pagination">
          <Button variant="ghost" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </Button>
          <div>
            <input
              type="number"
              className="pagination-input"
              value={pagintionInputVal}
              onChange={handlePaginationInputChange}
            />
            &nbsp;&nbsp;
            <span>{pageIndex + 1}/{pageCount}</span>
          </div>
          <Button variant="ghost" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </Button>
        </div> : null
      }
    </div>
  );
};

export default ReactTable;
